import { initializeApp } from 'firebase/app';
import {
  getAuth,
  connectAuthEmulator,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signInWithCustomToken,
  signOut,
  signInWithEmailAndPassword,
  verifyPasswordResetCode,
  confirmPasswordReset,
  type UserCredential,
} from 'firebase/auth';
import * as FirebaseAuth from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { getFunctions, type Functions } from 'firebase/functions';
import firebaseConfig from '../firebase-applet-config.json';
import { setKeepSignedInOnDevice } from './lib/authDevicePreference';
import {
  isEmulatorMode,
  isIsolatedSyntheticFixtureMode,
  isLocalMockMode,
  isSimulationE2eMode,
  EMULATOR_HOST,
  EMULATOR_PORTS,
} from './lib/localDev/isLocalMockMode';

export const app = initializeApp(firebaseConfig);
// The simulation browser gate must never initialize Firebase's hosted popup /
// redirect resolver. A resolver-free in-memory Auth instance is sufficient for
// imported code that reads `auth.currentUser`, while the hard DEV+LOCAL+E2E
// gate guarantees production always uses the normal persisted Auth instance.
export const auth = isSimulationE2eMode()
  ? FirebaseAuth.initializeAuth(app, { persistence: FirebaseAuth.inMemoryPersistence })
  : getAuth(app);

// Callable Functions client PINNED to us-west1, resolved LAZILY (memoized).
// Almost every app callable is deployed in us-west1 per CLAUDE.md; the SDK's
// `getFunctions(app)` defaults to us-central1 → us-west1 callables 404.
// LAZY (not a module-load `const`) so importing firebase.ts has no side effect:
// an eager `getFunctions(app,'us-west1')` at load broke tests that mock
// `firebase/app` but not `firebase/functions` (it hit `.getProvider` on a
// mocked app). Call `functionsWest1()` at use-time.
// NOTE: this region pin is necessary but NOT sufficient under this org's Domain
// Restricted Sharing policy (allUsers invoker is forbidden) — direct browser→
// callable invocation is blocked at the Cloud Run edge, so client callables
// must be routed through the server `/api/functions` proxy. Follow-up tracked.
let _functionsWest1: Functions | null = null;
export function functionsWest1(): Functions {
  return (_functionsWest1 ??= getFunctions(app, 'us-west1'));
}
// Named-database routing — `initializeFirestore(app, settings, databaseId)`
// looks superficially equivalent but the third arg is `@beta` in the SDK
// version pinned by package.json and (in practice) breaks auth-attached
// requests, so every Firestore read after sign-in returned `Missing or
// insufficient permissions`. `getFirestore(app, dbId)` is the stable path.
// Optional-undefined-property writes are handled with `stripUndefined()` at
// the call site (see store/index.ts).
export const db = getFirestore(app, firebaseConfig.firestoreDatabaseId);
export const storage = getStorage(app);

// LOCAL DEV — connect the client SDK to the Firebase Emulator Suite when
// VITE_USE_EMULATORS=1 (hard dev-gated via isEmulatorMode → stripped from prod
// builds). Real local Auth + Firestore (named DB) + Storage so the whole app
// runs offline with a real signed-in user and PERSISTENT writes. Runs at module
// load BEFORE any SDK use (required by connect*Emulator). Try/catch absorbs the
// "already connected" throw on HMR re-entry. See docs/LOCAL_DEV.md.
if (isIsolatedSyntheticFixtureMode() && !isEmulatorMode()) {
  throw new Error(
    'Isolated synthetic fixture mode requires VITE_USE_EMULATORS=1; refusing cloud Firebase initialization.',
  );
}

if (isEmulatorMode() || isSimulationE2eMode()) {
  try {
    // The hermetic simulation E2E gate never calls these providers, but pinning
    // every SDK endpoint to loopback prevents app-shell listeners from reaching
    // production merely because firebase.ts was imported.
    connectAuthEmulator(auth, `http://${EMULATOR_HOST}:${EMULATOR_PORTS.auth}`, { disableWarnings: true });
    connectFirestoreEmulator(db, EMULATOR_HOST, EMULATOR_PORTS.firestore);
    connectStorageEmulator(storage, EMULATOR_HOST, EMULATOR_PORTS.storage);
    // eslint-disable-next-line no-console
    console.warn(isEmulatorMode()
      ? '[emulators] client connected to local Auth/Firestore/Storage (DEV only).'
      : '[simulation-e2e] Firebase Auth/Firestore/Storage pinned to loopback (DEV only).');
  } catch (e) {
    const msg = (e as Error)?.message ?? String(e);
    if (isIsolatedSyntheticFixtureMode() && !/already/i.test(msg)) {
      throw e;
    }
    // eslint-disable-next-line no-console
    console.warn('[emulators] connect skipped (already connected / HMR):', msg);
  }
}

export const provider = new GoogleAuthProvider();

/** Popup failures that should fall back to full-page redirect (common on localhost). */
const POPUP_FALLBACK_CODES = new Set([
  'auth/popup-blocked',
  'auth/popup-closed-by-user',
  'auth/cancelled-popup-request',
  'auth/operation-not-supported-in-this-environment',
]);

function isPopupAuthFailure(error: unknown): boolean {
  if (
    typeof error === 'object'
    && error !== null
    && 'code' in error
    && POPUP_FALLBACK_CODES.has(String((error as { code: string }).code))
  ) {
    return true;
  }
  
  const msg = error instanceof Error ? error.message : String(error);
  if (msg.includes('Cross-Origin-Opener-Policy') || msg.includes('window.closed')) {
    return true;
  }
  
  return false;
}

export const signInWithGoogle = async (): Promise<UserCredential | void> => {
  try {
    return await signInWithPopup(auth, provider);
  } catch (error) {
    if (isPopupAuthFailure(error)) {
      console.warn(
        '[auth] Google popup sign-in unavailable, falling back to redirect',
        (error as { code?: string }).code,
      );
      await signInWithRedirect(auth, provider);
      return;
    }
    console.error('Error signing in with Google', error);
    throw error;
  }
};

let redirectHandled = false;

/** Resolve a pending Google redirect sign-in once per page load. */
export async function handleAuthRedirect(): Promise<UserCredential | null> {
  if (redirectHandled) return null;
  redirectHandled = true;
  // The DEV-only fixture shell has no hosted-auth session to complete. Calling
  // Firebase's redirect resolver here can wait on provider storage/network and
  // leave the local workbench with an empty root even though AuthGate will
  // intentionally bypass authentication. Production can never enter this
  // branch because isLocalMockMode() is hard-gated by import.meta.env.DEV.
  if (isLocalMockMode() || isSimulationE2eMode()) return null;
  try {
    return await getRedirectResult(auth);
  } catch (error) {
    console.error('[auth] redirect sign-in failed', error);
    return null;
  }
}

/** Dev helper — paste a custom token from `scripts/devSignInToken.ts`. */
export const signInWithCustomTokenDev = (token: string) => signInWithCustomToken(auth, token);

export const signInWithEmail = async (email: string, password: string) => {
  return signInWithEmailAndPassword(auth, email, password);
};

export const resetPassword = async (email: string) => {
  const response = await fetch('/api/auth/password-reset', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => ({})) as { error?: string };
    throw new Error(payload.error || 'Failed to request password reset');
  }
};

export const checkPasswordResetCode = (code: string): Promise<string> =>
  verifyPasswordResetCode(auth, code);

export const applyPasswordReset = (code: string, password: string): Promise<void> =>
  confirmPasswordReset(auth, code, password);

export const logOut = async () => {
  const uid = auth.currentUser?.uid;
  if (uid) {
    try { setKeepSignedInOnDevice(uid, false); }
    catch { /* Storage failure must never prevent explicit sign-out. */ }
  }
  try {
    await signOut(auth);
  } catch (error) {
    console.error("Error signing out", error);
  }
};

export enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

export interface FirestoreErrorInfo {
  error: string;
  operationType: OperationType;
  path: string | null;
  authInfo: {
    userId: string | undefined;
    email: string | null | undefined;
    emailVerified: boolean | undefined;
    isAnonymous: boolean | undefined;
    tenantId: string | null | undefined;
    providerInfo: {
      providerId: string;
      displayName: string | null;
      email: string | null;
      photoUrl: string | null;
    }[];
  }
}

export function handleFirestoreError(error: unknown, operationType: OperationType, path: string | null) {
  const errInfo: FirestoreErrorInfo = {
    error: error instanceof Error ? error.message : String(error),
    authInfo: {
      userId: auth.currentUser?.uid,
      email: auth.currentUser?.email,
      emailVerified: auth.currentUser?.emailVerified,
      isAnonymous: auth.currentUser?.isAnonymous,
      tenantId: auth.currentUser?.tenantId,
      providerInfo: auth.currentUser?.providerData.map(provider => ({
        providerId: provider.providerId,
        displayName: provider.displayName,
        email: provider.email,
        photoUrl: provider.photoURL
      })) || []
    },
    operationType,
    path
  }
  console.error('Firestore Error: ', JSON.stringify(errInfo));
  throw new Error(JSON.stringify(errInfo));
}
